import { GridColDef } from "@mui/x-data-grid-pro";
import { Button, IconButton } from "@mui/material";

import CustomItemStatus from "../../../../../../common/components/custom_item_status";
import dayjs from "dayjs";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

/**
 * GenerateOrdemComprasEntregaColumnDefinition
 */

type Props = {
  onActionEditar: (idOrdemCompra: number, openNewTab: boolean) => void;
  onActionExportar: (idOrdemCompra: number) => void;
  onActionEmail: (idOrdemCompra: number | null, openState: boolean) => void;
};

export const GenerateOrdemComprasEntregaColumnDefinition = ({
  onActionEditar,
  onActionExportar,
  onActionEmail,
}: Props): GridColDef[] => {
  const computeStatusTitle = (status: number) => {
    switch (status) {
      case 1:
        return "Entregue Totalmente";
      case 2:
        return "Entrega Parcial";
      case 3:
        return "Pendente";
      case 4:
        return "Atrasada";
      case 5:
        return "Cancelada";
      default:
        return "?";
    }
  };

  const computeStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return "green";
      case 2:
        return "yellow";
      case 3:
        return "gray";
      case 4:
        return "red";
      case 5:
        return "red";
      default:
        return "blue";
    }
  };

  const computeStatusDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Entregue Totalmente";
      case 2:
        return "Entrega Parcial";
      case 3:
        return "Pendente";
      case 4:
        return "Atrasada";
      case 5:
        return "Cancelada";
      default:
        return "?";
    }
  };

  // Entregue

  const computeStatusEntregueTitle = (status: number) => {
    switch (status) {
      case 1:
        return "Sim";
      case 2:
        return "Não";
      default:
        return "?";
    }
  };

  const computeStatusEntregueColor = (status: number) => {
    switch (status) {
      case 1:
        return "green";
      case 2:
        return "red";
      default:
        return "blue";
    }
  };

  const computeStatusEntregueDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Sim";
      case 2:
        return "Não";
      default:
        return "?";
    }
  };

  return [
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      minWidth: 145,
      width: 145,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <CustomItemStatus
            size="autowidth"
            textSize="verySmall"
            title={computeStatusTitle(value)}
            bgColor={computeStatusColor(value)}
            description={computeStatusDescription(value)} />
        </div>
      )
    },
    {
      field: "notaFiscalEntregue",
      filterable: false,
      headerName: "NF Entregue",
      minWidth: 80,
      width: 80,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <CustomItemStatus
            size="autowidth"
            textSize="small"
            title={computeStatusEntregueTitle(value === true ? 1 : 2)}
            bgColor={computeStatusEntregueColor(value === true ? 1 : 2)}
            description={computeStatusEntregueDescription(value === true ? 1 : 2)} />
        </div>
      )
    },
    {
      field: "nrOrdemCompra",
      filterable: false,
      headerName: "Nº OC",
      minWidth: 50,
      sortable: false,
    },
    {
      field: "dataRef",
      filterable: false,
      headerName: "Data OC",
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div>{value && dayjs.utc(value).local().format('DD/MM/YYYY')}</div>
        );
      },
    },
    {
      field: "nrColetaPreco",
      filterable: false,
      headerName: "Nº Coleta",
      minWidth: 50,
      sortable: false,
    },
    {
      field: "nomeFantasia",
      filterable: false,
      headerName: "Fornecedor",
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: "valorTotal",
      filterable: false,
      headerName: "Valor Total OC",
      minWidth: 120,
      sortable: false,
      renderCell: ({ value }) => <div>{value !== null ? `R$ ${value.toFixed(2).replace('.', ',')}` : null}</div>
    },
    {
      field: "centroCusto",
      filterable: false,
      headerName: "Centro de Custo",
      minWidth: 140,
      sortable: false,
      valueGetter: (_, row) => `${row['centroCustoCodigo']} - ${row['centroCustoDescricao']}`,
    },
    {
      field: "nrNotaFiscal",
      filterable: false,
      headerName: "Nº Nota Fiscal",
      minWidth: 140,
      sortable: false,
    },
    {
      field: "dataEntrega",
      filterable: false,
      headerName: "Data de Entrega",
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div>{value && dayjs.utc(value).local().format('DD/MM/YYYY')}</div>
        );
      },
    },
    {
      field: "notaMedia",
      filterable: false,
      headerName: "Nota",
      width: 75,
      sortable: false,
      renderCell: ({ value }) => <div>{value !== null ? value.toFixed(2).replace('.', ',') : null}</div>
    },
    {
      field: 'idOrdemCompra',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      width: 60,
      resizable: false,
      valueGetter: (_, row) => ([row['idOrdemCompra'], row['status']]),
      renderCell: ({ value: [idOrdemCompra, status] }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton
              onMouseDown={e => {
                if (e.button === 1) {
                  onActionEditar(idOrdemCompra, true);
                } else {
                  onActionEditar(idOrdemCompra, e.ctrlKey);
                }
                e.preventDefault();
              }}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>
          </div>
        );
      }
    },
    {
      field: "exportar",
      filterable: false,
      headerName: "Exportar",
      sortable: false,
      valueGetter: (_, row) => { return row['idOrdemCompra'] as number; },
      renderCell: ({ value }) => {
        return (
          <Button
            onMouseDown={() => { onActionExportar(value) }}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.2rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
            }}>
            Exportar
          </Button>
        );
      },
    },
    {
      field: 'email',
      sortable: false,
      filterable: false,
      headerName: 'Enviar Email',
      width: 65,
      valueGetter: (_, row) => row['idOrdemCompra'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => { onActionEmail(value, true) }}>
              <DynamicIcon nameIcon="EmailOutlined" size={25} className="text-[#009C4E]" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}